// Delivery-day maths. Pure: no React, router or Firebase imports, and nothing here reads the
// machine's time zone, so the results are the same under any TZ (tests also run under
// TZ=Pacific/Auckland). Time zones come from Intl with an explicit `timeZone`.
import type { Granularity } from '../config/products';

/** 'YYYY-MM-DD'. A calendar day; which instants it covers depends on the zone you apply to it. */
export type IsoDay = string;

/** ISO 8601 instants with a trailing Z, as /data-agg expects. */
export interface TimeWindow {
  start_time: string;
  end_time: string;
}

const ISO_DAY = /^(\d{4})-(\d{2})-(\d{2})$/;

/** Date.UTC for a calendar day, correct also for years 0 to 99 (Date.UTC maps those to 19xx). */
function utcMs(year: number, month1: number, day: number): number {
  const d = new Date(0);
  d.setUTCFullYear(year, month1 - 1, day);
  return d.getTime();
}

function ymd(ms: number): IsoDay {
  const d = new Date(ms);
  const pad = (n: number, w: number) => String(n).padStart(w, '0');
  return `${pad(d.getUTCFullYear(), 4)}-${pad(d.getUTCMonth() + 1, 2)}-${pad(d.getUTCDate(), 2)}`;
}

/** True for a real calendar day written 'YYYY-MM-DD' ('YYYY-02-30' is false). */
export const isIsoDay = (s: unknown): s is IsoDay => {
  if (typeof s !== 'string') return false;
  const m = ISO_DAY.exec(s);
  if (!m) return false;
  return ymd(utcMs(Number(m[1]), Number(m[2]), Number(m[3]))) === s;
};

function parseIsoDay(day: IsoDay): { y: number; m: number; d: number } {
  if (!isIsoDay(day)) throw new RangeError(`Invalid ISO day: ${String(day)}`);
  const m = ISO_DAY.exec(day) as RegExpExecArray;
  return { y: Number(m[1]), m: Number(m[2]), d: Number(m[3]) };
}

/** Calendar arithmetic on a day label. Month, year and leap-year boundaries included. */
export function addDays(day: IsoDay, n: number): IsoDay {
  const { y, m, d } = parseIsoDay(day);
  return ymd(utcMs(y, m, d + n));
}

const formatters = new Map<string, Intl.DateTimeFormat>();

function formatterFor(zone: string): Intl.DateTimeFormat {
  let f = formatters.get(zone);
  if (!f) {
    // Throws RangeError for an unknown zone; that is a registry bug, not a runtime condition.
    f = new Intl.DateTimeFormat('en-US', {
      timeZone: zone,
      hourCycle: 'h23',
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      second: 'numeric',
    });
    formatters.set(zone, f);
  }
  return f;
}

/** Offset of `zone` from UTC at instant `ms`, in ms (positive east of Greenwich). */
function zoneOffsetMs(ms: number, zone: string): number {
  const f: Record<string, number> = {};
  for (const p of formatterFor(zone).formatToParts(new Date(ms))) {
    if (p.type !== 'literal') f[p.type] = Number(p.value);
  }
  // Some engines still print midnight as hour 24 even with h23.
  const asIfUtc = utcMs(f.year, f.month, f.day) + (f.hour % 24) * 3_600_000 + f.minute * 60_000 + f.second * 1_000;
  return asIfUtc - ms;
}

/**
 * The instant (UTC ms) at which `day` begins in `zone`. Guess midnight as if the zone were UTC,
 * correct by the offset there, then correct once more by the offset at the corrected instant so
 * a day whose start sits on the other side of a DST change still lands right.
 */
export function zonedMidnightUtcMs(day: IsoDay, zone: string): number {
  const { y, m, d } = parseIsoDay(day);
  const guess = utcMs(y, m, d);
  const first = guess - zoneOffsetMs(guess, zone);
  return guess - zoneOffsetMs(first, zone);
}

/** [midnight(day), midnight(day + 1) - 1 ms] in `zone`. A DST day is 23 or 25 hours long. */
export function deliveryDayWindow(day: IsoDay, zone: string): TimeWindow {
  const start = zonedMidnightUtcMs(day, zone);
  const end = zonedMidnightUtcMs(addDays(day, 1), zone) - 1;
  return { start_time: new Date(start).toISOString(), end_time: new Date(end).toISOString() };
}

const TIME_ZONE_SUFFIX = /(?:Z|[+-]\d{2}(?::?\d{2})?)$/i;

/**
 * A BigQuery-style bucket value to UTC ms. Accepts 'YYYY-MM-DD HH:MM:SS', 'YYYY-MM-DD',
 * ISO 8601 with or without a zone, and the { value } wrapper the BigQuery client serialises.
 * Values with no zone are read as UTC. Anything else gives null.
 */
export function parseBucket(v: unknown): number | null {
  let raw: unknown = v;
  if (raw !== null && typeof raw === 'object' && 'value' in raw) raw = (raw as { value: unknown }).value;
  if (typeof raw !== 'string') return null;
  let s = raw.trim();
  if (!isIsoDay(s.slice(0, 10))) return null;
  if (s.length === 10) {
    s += 'T00:00:00Z';
  } else {
    if (!/^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}/.test(s)) return null;
    s = s.replace(' ', 'T');
    if (!TIME_ZONE_SUFFIX.test(s.slice(11))) s += 'Z';
  }
  const ms = Date.parse(s);
  return Number.isNaN(ms) ? null : ms;
}

export function bucketToIsoUtc(v: unknown): string | null {
  const ms = parseBucket(v);
  return ms === null ? null : new Date(ms).toISOString();
}

// Fixed tables rather than Intl: ICU data differs between Node versions ("Sep" vs "Sept").
const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] as const;
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'] as const;

/** 'Tue 14 Oct 2025'. An invalid day comes back unchanged. */
export function formatDayLabel(day: IsoDay): string {
  if (!isIsoDay(day)) return String(day);
  const { y, m, d } = parseIsoDay(day);
  const dt = new Date(utcMs(y, m, d));
  return `${WEEKDAYS[dt.getUTCDay()]} ${dt.getUTCDate()} ${MONTHS[dt.getUTCMonth()]} ${dt.getUTCFullYear()}`;
}

/** '14 Oct 2025, 08:00 UTC' for hourly, '14 Oct 2025' for daily. A non-finite input gives ''. */
export function formatUtc(ms: number, g: Granularity): string {
  if (!Number.isFinite(ms)) return '';
  const dt = new Date(ms);
  const date = `${dt.getUTCDate()} ${MONTHS[dt.getUTCMonth()]} ${dt.getUTCFullYear()}`;
  if (g === 'daily') return date;
  const hh = String(dt.getUTCHours()).padStart(2, '0');
  const mm = String(dt.getUTCMinutes()).padStart(2, '0');
  return `${date}, ${hh}:${mm} UTC`;
}

/** Calendar days first..last inclusive; [] when first > last. */
export function dayRange(first: IsoDay, last: IsoDay): IsoDay[] {
  const a = parseIsoDay(first);
  const b = parseIsoDay(last);
  const start = utcMs(a.y, a.m, a.d);
  const end = utcMs(b.y, b.m, b.d);
  const out: IsoDay[] = [];
  for (let ms = start, i = 0; ms <= end; i += 1) {
    out.push(ymd(ms));
    ms = utcMs(a.y, a.m, a.d + i + 1);
  }
  return out;
}

/** Whole days from a to b (b - a); negative when b is earlier. */
export function daysBetween(a: IsoDay, b: IsoDay): number {
  const pa = parseIsoDay(a);
  const pb = parseIsoDay(b);
  return Math.round((utcMs(pb.y, pb.m, pb.d) - utcMs(pa.y, pa.m, pa.d)) / 86_400_000);
}

/** '3 Sep' or, with year, '3 Sep 2026'. Invalid input is returned unchanged. */
export function formatShortDay(day: IsoDay, withYear = false): string {
  if (!isIsoDay(day)) return String(day);
  const { y, m, d } = parseIsoDay(day);
  return `${d} ${MONTHS[m - 1]}${withYear ? ` ${y}` : ''}`;
}

/**
 * 'Thu 3 Sep – Wed 9 Sep 2026' (same year), 'Wed 31 Dec 2025 – Thu 1 Jan 2026' (different years).
 * Equal days give the single-day label.
 */
export function formatDayRange(a: IsoDay, b: IsoDay): string {
  if (a === b || !isIsoDay(a) || !isIsoDay(b)) return a === b ? formatDayLabel(a) : `${a} – ${b}`;
  const sameYear = a.slice(0, 4) === b.slice(0, 4);
  const first = sameYear ? formatDayLabel(a).replace(/ \d{4}$/, '') : formatDayLabel(a);
  return `${first} – ${formatDayLabel(b)}`;
}
